/**
 * PocketFiler - Google Sign In Button
 * Uses Google Identity Services (GSI) SDK.
 * Handles both signup and login via the same flow.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';


const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';


export default function GoogleSignInButton({ mode = 'signin', referralCode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const buttonRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    // Load Google Identity Services script
    if (typeof window === 'undefined') return;
    if (window.google?.accounts?.id) {
      setScriptReady(true);
      return;
    }
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener('load', () => setScriptReady(true));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptReady(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!scriptReady || !window.google?.accounts?.id || !buttonRef.current) return;
    if (!GOOGLE_CLIENT_ID) {
      console.warn('Google SSO not configured — missing REACT_APP_GOOGLE_CLIENT_ID');
      return;
    }

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
      auto_select: false,
    });

    // Render the button
    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: 'outline',
      size: 'large',
      text: mode === 'signup' ? 'signup_with' : 'signin_with',
      logo_alignment: 'left',
      width: buttonRef.current?.offsetWidth || 320,
    });
  // eslint-disable-next-line
  }, [scriptReady, mode]);

  const handleCredentialResponse = async (response) => {
    if (!response?.credential) {
      toast.error(t('googleSSO.noCredential') || 'Google sign-in failed — no credential');
      return;
    }
    setLoading(true);
    try {
      const data = await api.post('/auth/google', {
        credential: response.credential,
        referral_code: referralCode || undefined,
      });

      if (data.requires_2fa) {
        toast.info(t('googleSSO.needs2fa') || 'Enter your 2FA code to complete sign in');
        // Redirect to a 2FA verification page with the temp token
        sessionStorage.setItem('pf_2fa_temp_token', data.temp_token);
        navigate('/login?2fa=1');
        return;
      }

      if (data.token) {
        localStorage.setItem('token', data.token);
        if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        if (refreshUser) await refreshUser();

        toast.success(t('googleSSO.success') || 'Welcome!');

        // Route based on onboarding state
        if (data.is_new_user) {
          navigate('/onboarding/welcome');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (e) {
      toast.error(e.message || t('googleSSO.error') || 'Google sign-in failed');
    }
    setLoading(false);
  };

  if (!GOOGLE_CLIENT_ID) {
    // Fallback if Google isn't configured
    return null;
  }

  return (
    <div className="w-full">
      <div ref={buttonRef} className="w-full flex justify-center" />
      {loading && (
        <p className="text-xs text-muted-foreground text-center mt-2">
          {t('googleSSO.signing') || 'Signing you in...'}
        </p>
      )}
    </div>
  );
}
